import React, {
  useCallback, useMemo, useState, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import SearchBarContainer from '../../../components/search-bar-container/search-bar-container.component';
import Menu from '../../../components/menu/menu.component';
import MenuItem from '../../../components/menu-item/menu-item.component';
import Toolbar from '../../../components/toolbar/toolbar.component';
import AppBar from '../../../components/app-bar/app-bar.component';
import Badge from '../../../components/badge/badge.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import IconOffsetBlock from '../../../components/icon-offset-block/icon-offset-block.component';
import NavigationMenuButton from '../../../components/navigation-menu-button/navigation-menu-button.component';
import HeaderSubmenuContainer from '../../../components/header-submenu-container/header-submenu-container.component';
import HeaderSubmenuItem from '../../../components/header-submenu-item/header-submenu-item.component';
import HeaderSubmenuHelper from '../../../components/header-submenu-helper/header-submenu-helper.component';
import LogoBlock from '../../../components/logo-block/logo-block.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ActionButton from '../../../components/action-button/action-button.component';
import ImageAvatar from '../../../components/image-avatar/image-avatar.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import SearchBar from '../search-bar/search-bar.component';
import HelpIcon from '../../../icons/help-icon';
import CommentIcon from '../../../icons/comment-icon';
import NotificationIcon from '../../../icons/notification-icon';
import SettingsIcon from '../../../icons/settings-icon';
import MainService from '../../../services/main-service';
import StructureService from '../../../services/structure-service';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import useScreenMobile from '../../../hooks/use-screen-mobile';
import useScreenSmall from '../../../hooks/use-screen-small';
import toggleNavigationDrawer from '../navigation/navigation.action';
import { toggleRightDrawer, toggleRightDrawerTab } from '../right-drawer/right-drawer.action';
import { readNotifications } from '../notifications/notifications.action';
import { readComments } from '../comments/comments.action';
import { resetFeedData } from '../app.action';
import {
  COMMENT_LIST_VIEW, CONTENT_PAGE_VIEW, DASHBOARD_VIEW, NOTIFICATION_LIST_VIEW, SETTINGS_PROFILE_VIEW,
} from '../../../constants/entity/permissions';
import {
  ROUTE_SETTINGS, ROUTE_LOGOUT, ROUTE_SETTINGS_PROFILE, ROUTE_SETTINGS_ACCOUNT, ROUTE_SETTINGS_ACCOUNT_LIST,
  ROUTE_SETTINGS_OPERATION_LOG, ROUTE_SETTINGS_DEAL_TEMPLATE_LIST, ROUTE_SETTINGS_PORTFOLIO_GROUP_LIST,
  ROUTE_SETTINGS_USER_LIST, ROUTE_SETTINGS_SECURITY, ROUTE_SETTINGS_NOTIFICATIONS, ROUTE_DASHBOARD,
} from '../../../constants/system/routes';
import PersonDefaultImage from '../../../static/images/person-default-image.png';

const Header: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const HELP_PAGE_ROUTE_MAIN: string = '/';
  const BUTTON_ITEMS_LIMIT: number = 100;
  const ANCHOR_BOTTOM: MenuAnchor = 'bottom';
  const ANCHOR_RIGHT: MenuAnchor = 'right';

  const {
    isRightDrawerOpen,
    isRightDrawerFirstTabOpen,
    isNavigationDrawerOpen,
    currentUser,
    newNotificationsTotal,
    newCommentsTotal,
  } = props;

  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isMobileScreen: boolean = useScreenMobile();
  const isSmallScreen: boolean = useScreenSmall();
  const permissionList: string[] = useCurrentUserPermissions();

  const currentRoute: string = location.pathname;
  const helpHostName: string | undefined = MainService.HELP_HOST;

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);

  const isNotificationListVisible: boolean = StructureService.checkPermission(NOTIFICATION_LIST_VIEW, permissionList);
  const isCommentListVisible: boolean = StructureService.checkPermission(COMMENT_LIST_VIEW, permissionList);

  const routeSettingsList: string[] = useMemo((): string[] => [
    ROUTE_SETTINGS, ROUTE_SETTINGS_ACCOUNT, ROUTE_SETTINGS_ACCOUNT_LIST, ROUTE_SETTINGS_OPERATION_LOG,
    ROUTE_SETTINGS_DEAL_TEMPLATE_LIST, ROUTE_SETTINGS_PORTFOLIO_GROUP_LIST, ROUTE_SETTINGS_USER_LIST,
    ROUTE_SETTINGS_PROFILE, ROUTE_SETTINGS_SECURITY, ROUTE_SETTINGS_NOTIFICATIONS,
  ], []);

  const openProfileMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const closeProfileMenu: MouseEventHandlerFunction = useCallback((): void => {
    setMenuAnchor(null);
  }, []);

  const commentButtonClick: Function = useCallback((): void => {
    dispatch(toggleRightDrawerTab(false));
    dispatch(resetFeedData());

    if (newCommentsTotal > 0) {
      dispatch(readComments());
    }
  }, [newCommentsTotal]);

  const notificationButtonClick: Function = useCallback((): void => {
    dispatch(toggleRightDrawerTab(true));
    dispatch(resetFeedData());

    if (newNotificationsTotal > 0) {
      dispatch(readNotifications());
    }
  }, [newNotificationsTotal]);

  const onNavigationDrawerOpen: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(toggleNavigationDrawer());
  }, []);

  const onMenuItemClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.preventDefault();
  }, []);

  const onToggleNavigationDrawer: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(toggleNavigationDrawer());
  }, []);

  const onToggleRightDrawer: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(toggleRightDrawer());
  }, []);

  const onLogoClick: MouseEventHandlerFunction = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    if (isSmallScreen) {
      if (isNavigationDrawerOpen) {
        onToggleNavigationDrawer(event);
      }

      if (isRightDrawerOpen) {
        onToggleRightDrawer(event);
      }
    }
  }, [isSmallScreen, isNavigationDrawerOpen, isRightDrawerOpen]);

  const profileMenuItems: ProfileMenuItem[] = useMemo((): ProfileMenuItem[] => [
    {
      id: 1,
      text: currentUser ? currentUser.fullName : 'User',
    },
    {
      id: 2,
      text: 'Profile',
      link: ROUTE_SETTINGS_PROFILE,
    },
    {
      id: 3,
      text: 'Sign Out',
      link: ROUTE_LOGOUT,
    },
  ], [currentUser]);

  const subMenuItems: NavigationMenuItem[] = useMemo((): NavigationMenuItem[] => [
    {
      id: 1,
      icon: (
        <Tooltip title="Comments">
          <IconOffsetBlock offsetTop={1}>
            <CommentIcon />
          </IconOffsetBlock>
        </Tooltip>
      ),
      itemsTotal: newCommentsTotal,
      isResponsive: true,
      onClick: commentButtonClick,
      permission: COMMENT_LIST_VIEW,
      isActive: isRightDrawerOpen && (!isRightDrawerFirstTabOpen || !isNotificationListVisible),
      isRightDrawerItem: true,
    },
    {
      id: 2,
      icon: (
        <Tooltip title="Notifications">
          <NotificationIcon />
        </Tooltip>
      ),
      itemsTotal: newNotificationsTotal,
      isResponsive: true,
      onClick: notificationButtonClick,
      permission: NOTIFICATION_LIST_VIEW,
      isActive: isRightDrawerOpen && isRightDrawerFirstTabOpen && isNotificationListVisible,
      isRightDrawerItem: true,
    },
    {
      id: 3,
      icon: (
        <Tooltip title="Settings">
          <SettingsIcon />
        </Tooltip>
      ),
      link: ROUTE_SETTINGS,
      isResponsive: false,
      permission: SETTINGS_PROFILE_VIEW,
      isActive: routeSettingsList.includes(currentRoute),
      isRightDrawerItem: false,
    },
    {
      id: 4,
      icon: (
        <Tooltip title="Help">
          <HelpIcon />
        </Tooltip>
      ),
      link: helpHostName ? `${helpHostName}${HELP_PAGE_ROUTE_MAIN}` : undefined,
      isResponsive: false,
      permission: CONTENT_PAGE_VIEW,
      isActive: false,
      isRightDrawerItem: false,
      isExternal: true,
    },
    {
      id: 5,
      icon: (
        <Tooltip title="Profile">
          <ImageAvatar
            link={currentUser && (currentUser.photoThumbnail || currentUser.photo)
              ? currentUser.photoThumbnail || currentUser.photo : PersonDefaultImage}
            text={currentUser ? currentUser.fullName : 'User'}
          />
        </Tooltip>
      ),
      isResponsive: false,
      menu: (
        <Menu
          isFlat
          isBordered
          isListExtended
          withListNoPadding
          menuAnchor={menuAnchor}
          onClick={closeProfileMenu}
          onClose={closeProfileMenu}
          anchorOrigin={{
            vertical: ANCHOR_BOTTOM,
            horizontal: ANCHOR_RIGHT,
          }}
        >
          {
            profileMenuItems.map((menuItem: ProfileMenuItem): ReactElement => (
              <FilteredLink
                isBlack
                key={menuItem.id}
                link={menuItem.link || null}
                onClick={menuItem.link ? closeProfileMenu : null}
              >
                <MenuItem
                  isBold={!menuItem.link}
                  isInactive={!menuItem.link}
                  withNoWrap={!menuItem.link}
                  withNoRipple={!menuItem.link}
                  isActive={!!menuItem.link && currentRoute === menuItem.link}
                  onClick={!menuItem.link ? onMenuItemClick : undefined}
                >
                  {menuItem.text}
                </MenuItem>
              </FilteredLink>
            ))
          }
        </Menu>
      ),
      onClick: openProfileMenu,
      permission: SETTINGS_PROFILE_VIEW,
      isRightDrawerItem: false,
    },
  ], [
    location, newCommentsTotal, newNotificationsTotal, isRightDrawerOpen, isMobileScreen,
    isRightDrawerFirstTabOpen, currentUser, menuAnchor, currentRoute, isNotificationListVisible,
    routeSettingsList,
  ]);

  return (
    <AppBar isActive={!!currentUser}>
      {
        !currentUser ? (
          <LogoBlock route={ROUTE_DASHBOARD} permission={DASHBOARD_VIEW} onClick={onLogoClick} />
        ) : (
          <Toolbar>
            <FlexContainer>
              <NavigationMenuButton onClick={onNavigationDrawerOpen} />
              <LogoBlock route={ROUTE_DASHBOARD} permission={DASHBOARD_VIEW} onClick={onLogoClick} />
            </FlexContainer>
            <FlexContainer>
              <SearchBarContainer>
                {
                  !isMobileScreen && (
                    <SearchBar />
                  )
                }
              </SearchBarContainer>
              <HeaderSubmenuContainer isLimited={!isNotificationListVisible && !isCommentListVisible}>
                {
                  subMenuItems.map((subMenuItem: NavigationMenuItem): ReactElement => (
                    <HeaderSubmenuItem
                      isActive={subMenuItem.isRightDrawerItem && subMenuItem.isActive}
                      key={subMenuItem.id}
                    >
                      <CheckAccess permissions={[subMenuItem.permission]}>
                        {
                          ((!subMenuItem.isResponsive && !isMobileScreen) || subMenuItem.isResponsive) && (
                            <FilteredLink isExternal={subMenuItem.isExternal} link={subMenuItem.link}>
                              <ActionButton
                                withNoRipple
                                withNoHighlight
                                isActive={subMenuItem.isActive}
                                onClick={subMenuItem.onClick}
                              >
                                {
                                  subMenuItem.itemsTotal ? (
                                    <Badge
                                      isActive={subMenuItem.isActive}
                                      content={subMenuItem.itemsTotal >= BUTTON_ITEMS_LIMIT
                                        ? `${BUTTON_ITEMS_LIMIT - 1}+` : subMenuItem.itemsTotal}
                                    >
                                      {subMenuItem.icon}
                                    </Badge>
                                  ) : subMenuItem.icon
                                }
                              </ActionButton>
                            </FilteredLink>
                          )
                        }
                        {subMenuItem.menu}
                        {
                          subMenuItem.isRightDrawerItem && subMenuItem.isActive && (
                            <HeaderSubmenuHelper />
                          )
                        }
                      </CheckAccess>
                    </HeaderSubmenuItem>
                  ))
                }
              </HeaderSubmenuContainer>
            </FlexContainer>
          </Toolbar>
        )
      }
    </AppBar>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    navigation,
    rightDrawer,
  } = reducers;

  return {
    currentUser: app.baseData && app.baseData.currentUser,
    newNotificationsTotal: app.baseData && app.baseData.newNotificationsTotal,
    newCommentsTotal: app.baseData && app.baseData.newCommentsTotal,
    isRightDrawerFirstTabOpen: rightDrawer.isFirstTabOpen,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    isNavigationDrawerOpen: navigation.isDrawerOpen,
  };
};

export default connect(mapState)(Header);
